import React from 'react';
import { useTranslation } from 'react-i18next';
import { Slider } from 'primereact/slider';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { GeminiOptionsInterface } from '../../../../app/interfaces/ProviderOptionsInterface';

export interface GeminiOptionsProps {
  options: GeminiOptionsInterface;
  onChange: (options: GeminiOptionsInterface) => void;
}

const GeminiOptions: React.FunctionComponent<GeminiOptionsProps> = (props: GeminiOptionsProps) => {
  const { options, onChange } = props;
  const { t } = useTranslation();

  // Define safety categories and thresholds
  const safetyCategories = [
    { label: t('provider_options.gemini.safety_categories.harassment'), value: 'HARM_CATEGORY_HARASSMENT' },
    { label: t('provider_options.gemini.safety_categories.hate_speech'), value: 'HARM_CATEGORY_HATE_SPEECH' },
    { label: t('provider_options.gemini.safety_categories.sexually_explicit'), value: 'HARM_CATEGORY_SEXUALLY_EXPLICIT' },
    { label: t('provider_options.gemini.safety_categories.dangerous'), value: 'HARM_CATEGORY_DANGEROUS_CONTENT' }
  ];

  const thresholdLevels = [
    { label: t('provider_options.gemini.threshold_levels.block_none'), value: 'BLOCK_NONE' },
    { label: t('provider_options.gemini.threshold_levels.block_low'), value: 'BLOCK_LOW_AND_ABOVE' },
    { label: t('provider_options.gemini.threshold_levels.block_medium'), value: 'BLOCK_MEDIUM_AND_ABOVE' },
    { label: t('provider_options.gemini.threshold_levels.block_high'), value: 'BLOCK_ONLY_HIGH' }
  ];

  // Get the current safety settings or initialize defaults
  const getSafetySettings = () => {
    return options.safetySettings || [];
  };

  // Handle selected safety categories change
  const handleCategoriesChange = (selectedCategories) => {
    const currentSettings = getSafetySettings();
    
    // Remove any categories that are no longer selected
    const filteredSettings = currentSettings.filter(setting => 
      selectedCategories.includes(setting.category)
    );
    
    // Add new categories with default threshold
    selectedCategories.forEach(category => {
      if (!filteredSettings.some(setting => setting.category === category)) {
        filteredSettings.push({
          category,
          threshold: 'BLOCK_MEDIUM_AND_ABOVE'
        });
      }
    });
    
    onChange({
      ...options,
      safetySettings: filteredSettings
    });
  };

  // Handle threshold change for a specific category
  const handleThresholdChange = (category, threshold) => {
    const currentSettings = getSafetySettings();
    
    const updatedSettings = currentSettings.map(setting => {
      if (setting.category === category) {
        return { ...setting, threshold };
      }
      return setting;
    });
    
    onChange({
      ...options,
      safetySettings: updatedSettings
    });
  };

  // Get the currently selected categories
  const getSelectedCategories = () => {
    return getSafetySettings().map(setting => setting.category);
  };

  // Get the threshold for a specific category
  const getThresholdForCategory = (category) => {
    const setting = getSafetySettings().find(s => s.category === category);
    return setting ? setting.threshold : 'BLOCK_MEDIUM_AND_ABOVE';
  };

  return (
    <div className="gemini-options">
      <h4 className="text-lg mb-2">{t('provider_options.gemini.title')}</h4>
      
      <div className="p-field mb-3">
        <label className="block mb-1">
          {t('provider_options.gemini.safety_categories.label')}
        </label>
        <MultiSelect
          value={getSelectedCategories()}
          options={safetyCategories}
          onChange={(e) => handleCategoriesChange(e.value)}
          placeholder={t('provider_options.gemini.safety_categories.placeholder')}
          className="w-full"
        />
      </div>

      {getSelectedCategories().length > 0 && (
        <div className="p-field mb-3">
          <label className="block mb-2">
            {t('provider_options.gemini.threshold_settings')}
          </label>
          
          {getSelectedCategories().map(category => {
            const categoryLabel = safetyCategories.find(c => c.value === category)?.label;
            
            return (
              <div key={category} className="mb-2 p-2 border-1 border-gray-200 rounded">
                <div className="text-sm font-medium mb-1">{categoryLabel}</div>
                <Dropdown
                  value={getThresholdForCategory(category)}
                  options={thresholdLevels}
                  onChange={(e) => handleThresholdChange(category, e.value)}
                  className="w-full"
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GeminiOptions;